import {Easing} from '@fantasmagames/core/src/tween/Easing';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';
import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {IInputListener, IInputController} from '@fantasmagames/core/src/interaction/DraggingComponent';

export class DragComponent implements IInputListener, IInputController {
	public static readonly RESTORE_ANIMATION_DURATION: number = 200;

	protected _maxAcceleration: number = 900;

	protected _baseYPosition: number = 0;
	protected _baseXPosition: number = 0;

	// TODO: Needs to check PPI / DPI
	protected _maxYDrag: number = 300;
	protected _minYDrag: number = -300;

	protected _maxXDrag: number = 0;
	protected _minXDrag: number = 0;

	protected _targetContainer: PIXI.Container; // Which object should be moved?P
	protected _interactionContainer: PIXI.Container; // Which object is interactive?
	protected _interactionContainerBasePositionY: number = 0;

	protected _interactionMaxYDrag: number = 300;
	protected _interactionMinYDrag: number = -300;
	protected _interactionMaxXDrag: number = 0;
	protected _interactionMinXDrag: number = 0;

	protected _canDrag: boolean;
	protected _isDragging: boolean;

	protected _dragStartY: number;
	protected _dragStartX: number;
	protected _dragStartTime: number;

	protected _containerDragStartY: number;
	protected _containerDragStartX: number;

	protected _isMobile: boolean;

	protected _restoreOnDragEnd: boolean = false;

	protected _endDragTween: Tween;
	protected _restoreTween: Tween;

	protected _endDragTweenDuration: number = 500;
	protected _endDragTweenEasing: Function = Easing.Cubic.Out;

	protected _lastDragMoveTime: number;
	protected _lastDragMoveYPosition: number = 0;
	protected _lastDragMoveXPosition: number = 0;

	public _listeningComponents: IInputListener[] = [];
	public _controllingComponents: IInputController[] = [];

	// protected _useMouseScroll: boolean = false;

	protected _updateManager: UpdateManager;

	// private _scroll: MouseWheelEvent;
	private _updateInteractiveContainerPosition: boolean = true;

	protected _dragStartOffsetPosition: Position;
	protected onDragStopBoundFunction: (this: Document, ev: TouchEvent) => any;

	constructor(updateManager: UpdateManager, draggingContainer: PIXI.Container, interactiveContainer: PIXI.Container, isMobile: boolean) {
		this._updateManager = updateManager;
		this._targetContainer = draggingContainer;
		this._interactionContainer = interactiveContainer;
		this._isMobile = isMobile;

		this.onDragStopBoundFunction = this.onDragStop.bind(this);

		// if (!this._isMobile && this._useMouseScroll) {
		// 	this._scroll = new MouseWheelEvent(interactiveContainer, this.onScroll.bind(this));
		// }

		this.initialize();
	}

	protected initialize(): void {
		this._interactionContainer.on('touchstart', this.onDragStart.bind(this));
		this._interactionContainer.on('mousedown', this.onDragStart.bind(this));
	}

	public enable(): void {
		this.canDrag = true;
	}

	public disable(): void {
		this.canDrag = false;
	}

	protected onDragStart(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		if (!this.canDrag) {
			return;
		}

		this.finishDragTween();

		this._containerDragStartY = this._targetContainer.y;
		this._containerDragStartX = this._targetContainer.x;

		this._dragStartY = event.data.global.y;
		this._dragStartX = event.data.global.x;
		const interactionGlobalPos: Position = this._interactionContainer.getGlobalPosition();
		// this._dragStartOffsetPosition = {x: event.data.global.x - this._interactionContainer.x - interactionGlobalPos.x, y: this._dragStartY - this._interactionContainer.y - interactionGlobalPos.y};
		this._dragStartOffsetPosition = {
			x: this._interactionContainer.x - event.data.global.x,
			y: this._interactionContainer.y - this._dragStartY,
		};

		this._dragStartTime = event.data.originalEvent.timeStamp;

		this._lastDragMoveTime = Math.floor(event.data.originalEvent.timeStamp * 0.005);
		this._lastDragMoveYPosition = event.data.global.y;
		this._lastDragMoveXPosition = event.data.global.x;

		// this._interactionContainer.on('touchend', this.onDragStop.bind(this));
		document.addEventListener('touchend', this.onDragStopBoundFunction);
		// this._interactionContainer.on('touchcancel', this.onDragStop.bind(this));
		document.addEventListener('touchcancel', this.onDragStopBoundFunction);
		// this._interactionContainer.on('touchendoutside', this.onDragStop.bind(this));
		this._interactionContainer.on('touchmove', this.onDragMoveEvent.bind(this));

		this._interactionContainer.on('mousemove', this.onDragMoveEvent.bind(this));
		// this._interactionContainer.on('mouseup', this.onDragStop.bind(this));
		document.addEventListener('mouseup', this.onDragStopBoundFunction);

		// this._interactionContainer.on('mouseout', this.onDragStop.bind(this));

		if (this.isMobile) {
		} else {
			// this._interactionContainer.on('rightup', this.onDragStop.bind(this));
			document.addEventListener('rightup', this.onDragStopBoundFunction);
		}

		this.draggingStarted(event);
	}

	protected finishDragTween(): void {
		if (this._endDragTween && this._endDragTween.isPlaying) {
			this._endDragTween.stop();
			this._endDragTween = null;
		}
	}

	protected onDragStop(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		if (!this.isDragging) {
			return;
		}

		this.draggingFinished();
	}

	public onForceStopDrag(): void {
		if (!this.isDragging) {
			return;
		}

		this.draggingFinished();
	}

	protected onCancelDrag(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		if (!this.isDragging) {
			return;
		}
	}

	protected onDragMoveEvent(event: PIXI.interaction.InteractionEvent, ...args: any[]): void {
		const deltaY: number = event.data.global.y - this._dragStartY;
		const deltaX: number = event.data.global.x - this._dragStartX;

		const currentTime = Math.floor(event.data.originalEvent.timeStamp * 0.005);

		if (currentTime > this._lastDragMoveTime) {
			this._lastDragMoveTime = currentTime;
			this._lastDragMoveYPosition = event.data.global.y;
			this._lastDragMoveXPosition = event.data.global.x;
		}

		if (!this._updateInteractiveContainerPosition) {
			const newY: number = Math.max(Math.min(deltaY + this._containerDragStartY, this._maxYDrag), this._minYDrag);
			const newX: number = Math.max(Math.min(deltaX + this._containerDragStartX, this._maxXDrag), this._minXDrag);

			this.updateDraggingContainer(newY, newX, 0);
		}
		this.updateInteractiveContainer(event.data.global.y, event.data.global.x, 0);
	}

	protected updateInteractiveContainer(y: number, x: number, fraction: number): void {
		if (this._updateInteractiveContainerPosition) {
			y = y + this._dragStartOffsetPosition.y;
			const fractY = Math.max(0, Math.min(1, GameUtils.normalize(y, this._interactionMaxYDrag, this._interactionMinYDrag)));
			const newY = GameUtils.lerp(this._interactionMinYDrag, this._interactionMaxYDrag, fractY);

			this._interactionContainer.y = newY;
			// this._interactionContainer.x = fractX;

			const targetNewY: number = GameUtils.lerp(this._minYDrag, this._maxYDrag, 1 - fractY);
			// const targetNewX: number = GameUtils.lerp(this._minXDrag, this._maxXDrag, 0);
			const targetNewX: number = Math.max(Math.min(x - this._dragStartX + this._containerDragStartX, this._maxXDrag), this._minXDrag);

			this.updateDraggingContainer(targetNewY, targetNewX, 0);
		}
	}

	protected clearListeners(): void {
		// this._interactionContainer.removeListener('touchend');
		document.removeEventListener('touchend', this.onDragStopBoundFunction);
		// this._interactionContainer.removeListener('touchendoutside');
		this._interactionContainer.removeListener('touchmove');
		// this._interactionContainer.removeListener('touchcancel');
		document.removeEventListener('touchcancel', this.onDragStopBoundFunction);

		this._interactionContainer.removeListener('mousemove');
		// this._interactionContainer.removeListener('mouseup');
		document.removeEventListener('mouseup', this.onDragStopBoundFunction);
		// this._interactionContainer.removeListener('mouseout');

		if (this._isMobile) {
		} else {
			// this._interactionContainer.removeListener('rightup');
			document.removeEventListener('rightup', this.onDragStopBoundFunction);
		}
	}

	protected draggingStarted(event: PIXI.interaction.InteractionEvent): void {
		this.isDragging = true;
	}

	protected draggingFinished(): void {
		this.clearListeners();

		this._dragStartX = null;
		this._dragStartY = null;
	}

	protected updateDraggingContainer(newPositionY: number, newPositionX: number, fraction: number): void {
		this._targetContainer.y = newPositionY;
		// this._targetContainer.x = newPositionX;

		const fractX = GameUtils.normalize(newPositionX, this._maxXDrag, this._minXDrag);
		const fractY = GameUtils.normalize(newPositionY, this._maxYDrag, this._minYDrag);

		const data = {x: newPositionX, y: newPositionY, fractX, fractY};

		this.updateListeners(data);
	}

	public updateListeners(data: any): void {
		// tslint:disable-next-line: prefer-for-of
		for (let item = 0; item < this._listeningComponents.length; item++) {
			const component = this._listeningComponents[item];
			component.updateFromController(data);
			// }
		}
	}

	public addlisteningComponent(component: any): void {
		if (typeof component !== 'object') {
			// console.log('Not a valid component. Needs to be of type object.');

			return;
		}

		this.bindListener(component);
	}

	public bindListener(object: any): void {
		this._listeningComponents.push(object);
	}

	public unbindListener(object: any): void {
		const index = this._listeningComponents.indexOf(object);
		this._listeningComponents.splice(index, 1);
	}

	public removeListeningComponent(component: any): boolean {
		if (typeof component !== 'object' || this._listeningComponents.indexOf(component) === -1) {
			return false;
		}

		const index = this._listeningComponents.indexOf(component);
		this._listeningComponents.splice(index, 1);

		return true;
	}

	public addControllerComponent(component: IInputController): void {
		if (typeof component !== 'object') {
			return;
		}

		component.bindListener.bind(this);
		this._controllingComponents.push(component);
	}

	public removeControllerComponent(component: any): boolean {
		if (typeof component !== 'object' || this._controllingComponents.indexOf(component) === -1) {
			return false;
		}

		const index = this._controllingComponents.indexOf(component);
		component.unbindListener(this);
		this._controllingComponents.splice(index, 1);

		return true;
	}

	public updateFromController(data: any): void {}

	protected onScroll(delta: Position): void {
		const newPosY: number = Math.max(Math.min(this._targetContainer.y - delta.y, this._maxYDrag), this._minYDrag);
		const newPosX: number = Math.max(Math.min(this._targetContainer.x + delta.x, this._maxXDrag), this._minXDrag);

		this.finishDragTween();

		this.updateDraggingContainer(newPosY, newPosX, 0);
	}

	public resetPosition(): void {
		this._interactionContainer.y = this.interactiveContainerBasePositionY;
		// this._targetContainer.y = this._baseYPosition;
	}

	public set interactiveContainerBasePositionY(v: number) {
		this._interactionContainerBasePositionY = v;
	}

	public get interactiveContainerBasePositionY(): number {
		return this._interactionContainerBasePositionY;
	}

	public set isDragging(v: boolean) {
		this._isDragging = v;
	}

	public get isDragging(): boolean {
		return this._isDragging;
	}

	public set canDrag(v: boolean) {
		this._canDrag = this._interactionContainer.interactive = v;
	}

	public get canDrag(): boolean {
		return this._canDrag;
	}

	public set isMobile(value: boolean) {
		this._isMobile = value;
	}

	public get isMobile(): boolean {
		return this._isMobile;
	}

	public get updateManager(): UpdateManager {
		return this._updateManager;
	}

	public set baseYPosition(posY: number) {
		this._baseYPosition = posY;
	}

	public set baseXPosition(posX: number) {
		this._baseXPosition = posX;
	}

	public set interactionMinY(pos: number) {
		this._interactionMinYDrag = pos;
	}

	public set interactionMinX(pos: number) {
		this._interactionMinXDrag = pos;
	}

	public set interactionMaxY(pos: number) {
		this._interactionMaxYDrag = pos;
	}

	public set interactionMaxX(pos: number) {
		this._interactionMaxXDrag = pos;
	}

	public set minY(pos: number) {
		this._minYDrag = pos;
	}

	public set minX(pos: number) {
		this._minXDrag = pos;
	}

	public set maxY(pos: number) {
		this._maxYDrag = pos;
	}

	public set maxX(pos: number) {
		this._maxXDrag = pos;
	}

	public set endDragTweenEasing(easing: any) {
		this._endDragTweenEasing = easing;
	}

	public set endDragTweenDuration(duration: number) {
		this._endDragTweenDuration = duration;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/interaction/DragComponent.ts